import styled, { css } from 'styled-components'
import { device, fonts } from '../../../../styles/GlobalVariables'
import { IPlayerStyle } from '../type'
import Link from '../../../Link/Link'

export const Overlay = styled.div<{
  theme: IPlayerStyle
  showNextVideoTip: boolean
}>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  z-index: 11;
  pointer-events: none;
  padding: ${props => props.theme.overlayPaddingMobile ?? 12}px
    ${props => props.theme.overlayPaddingMobile ?? 20}px
    ${props =>
      (props.theme.overlayPaddingMobile ?? 12) +
      40 +
      (props.theme.seekBar == 'stacked' ? 17 : 0) +
      (props.showNextVideoTip ? 34 : 0)}px;

  ${device.medium} {
    padding: ${props => props.theme.overlayPadding ?? 20}px
      ${props => props.theme.overlayPadding ?? 20}px
      ${props =>
        (props.theme.overlayPadding ?? 20) +
        20 +
        (props.theme.seekBar == 'stacked' ? 17 : 0)}px;
  }
`

export const OverlayTopButtonsWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  direction: rtl;
  height: 30px;
  width: 100%;
  padding: 5px;

  ${device.small} {
    padding: 0;
    height: 40px;
  }
`
export const OverlayLeftButtonsWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  width: 100%;
`
export const OverlayRightButtonsWrap = styled.div``
const TOUCH_HIT_SLOP = 12 // extra px around each button for easier tapping

/** a real button (keyboard, screen readers) that looks like the old div */
const buttonReset = css`
  background: none;
  border: 0;
  color: inherit;
  font: inherit;

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
`

export const OverlayCloseButtonItem = styled.button<{ iconSize?: number }>`
  ${buttonReset}
  cursor: pointer;
  display: flex;
  pointer-events: auto;
  padding: ${TOUCH_HIT_SLOP - 2}px;
  margin: -${TOUCH_HIT_SLOP - 2}px;
  /* remove touch highlight */
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  user-select: none;

  ${device.small} {
    padding: ${TOUCH_HIT_SLOP}px;
    margin: -${TOUCH_HIT_SLOP}px;
  }

  /**
   * RESH13-1138 - the shared icon is 35px, 40px from the small breakpoint,
   * which the item-page design does not follow: there both the close and the
   * sound button are 30px. Only set where a size is asked for, so the reels
   * feed keeps the icon size it has.
   */
  ${props =>
    props.iconSize &&
    css`
      svg {
        width: ${props.iconSize}px;
      }

      ${device.small} {
        svg {
          width: ${props.iconSize}px;
        }
      }
    `}
`
export const OverlayInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${props => props.theme.overlayInfoGap ?? 14}px;
  pointer-events: none;

  /* display: flex would otherwise win over the hidden attribute */
  &[hidden] {
    display: none;
  }

  direction: rtl; // needed for text-align right
  padding-bottom: ${props =>
    props.theme.overlayInfoPaddingBottomMobile ?? 12}px;

  ${device.medium} {
    padding-bottom: ${props => props.theme.overlayInfoPaddingBottom ?? 20}px;
  }
`

export const PlayerLinkInline = styled(Link)`
  flex-shrink: 0;
  font-family: ${fonts.narkis};
  font-size: 16px;
  font-weight: 500;
  color: #fff !important;
  white-space: nowrap;
  border: 0.5px solid #fff;
  border-radius: 6px;
  padding: 0px 6px;
  align-self: flex-start;
  pointer-events: auto;

  &:visited {
    color: #fff !important;
  }
`

export const PlayerTipNextButton = styled.div<{ mobileOnly?: boolean }>`
  position: absolute;
  right: 0;
  bottom: 10px;
  width: 100%;
  z-index: 9999;
  height: 37px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transition: all 0.3s ease;

  p {
    font-family: ${fonts.narkis};
    color: #fff;
    text-align: center;
    font-size: 14px;
    font-weight: 700;
    line-height: 120%;
    text-shadow: 0 9px 9px rgba(0, 0, 0, 0.4);
  }
  ${props =>
    props.mobileOnly &&
    css`
      display: flex;

      ${device.small} {
        display: none;
      }
    `}
`

/** RESH13-1138 - 50x50 button with a 40x40 glyph, per the item-page design */
export const CenterPlayButton = styled.button`
  ${buttonReset}
  padding: 0;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  cursor: pointer;
  pointer-events: auto;
  filter: drop-shadow(0 9px 4.5px rgba(0, 0, 0, 0.4));
  -webkit-tap-highlight-color: transparent;

  svg {
    width: 40px;
    height: 40px;
  }
`

export const PlayerTitle = styled.div<{
  timelineItemTime?: string
  isMiniSeries: boolean
  titleFontSize?: number
}>`
  position: relative;
  line-height: 120%;
  font-family: ${fonts.narkis};
  color: #fff;
  text-align: right;
  font-weight: 700;
  text-shadow: 0 9px 9px rgba(0, 0, 0, 0.4);
  /* RESH13-1138 - the item page asks for 30px on both breakpoints */
  font-size: ${props => props.titleFontSize ?? 20}px;
  font-style: normal;
  max-height: 108px;

  display: -webkit-box;
  -webkit-line-clamp: ${props =>
    props.timelineItemTime || props.isMiniSeries ? 2 : 3};
  -webkit-box-orient: vertical;
  overflow: hidden;
  ${props => props.timelineItemTime && 'max-width: 69%;'};

  ${device.medium} {
    font-size: ${props => props.titleFontSize ?? 18}px;
  }
`

export const PlayerTitleLinkRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 20px;
  width: 100%;
  min-width: 0;
  justify-content: space-between;
  pointer-events: auto;
`

export const VideoTime = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: fit-content;
  height: 28px;
  padding: 2px 8px;
  background-color: #1fb5f9;
  border-radius: 6px;
  margin-bottom: 4px;

  span {
    font-family: ${fonts.narkis};
    font-size: 20px;
    color: #fff;
    text-align: center;
    font-weight: 600;
    line-height: 120%;
  }
`
